/**
 * Session ID Management
 * Uses cookies to persist session across page loads
 */

const SESSION_COOKIE = '__rc_sid';
const DEFAULT_TTL = 1800; // 30 minutes in seconds

/**
 * Generate a simple UUID v4
 */
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get cookie value by name
 */
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;

  const match = document.cookie.match(new RegExp(`(^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[2]) : null;
}

/**
 * Set cookie with name, value, and max-age
 */
function setCookie(name: string, value: string, maxAge: number): void {
  if (typeof document === 'undefined') return;

  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax${secure}`;
}

/**
 * Get or create session ID
 * Refreshes TTL on each call (extends session on activity)
 */
export function getSessionId(ttl: number = DEFAULT_TTL): string {
  let sid = getCookie(SESSION_COOKIE);

  if (!sid) {
    sid = `sess_${generateUUID()}`;
    setCookie(SESSION_COOKIE, sid, ttl);
  } else {
    // Refresh TTL on activity
    setCookie(SESSION_COOKIE, sid, ttl);
  }

  return sid;
}

/**
 * Clear session (for logout or reset)
 */
export function clearSession(): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${SESSION_COOKIE}=; path=/; max-age=0`;
}
